'use client';

import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowRight, UserCircle, BriefcaseBusiness, Building, CheckCircle2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

// Interface entfernt, da ungenutzt
// interface TargetGroup {
//   id: string;
//   title: string;
//   icon: React.ElementType;
//   image: string;
//   content: string;
//   cta: string;
//   link: string;
// }

interface TabContentProps {
  title: string;
  description: string;
  benefits: string[];
  ctaText: string;
  ctaLink: string;
}

function TabContent({ title, description, benefits, ctaText, ctaLink }: TabContentProps) {
  const isEmployerContent = title.includes("Unternehmen");
  const titleColor = isEmployerContent ? "text-true-blue" : "text-thistle";
  const accentColor = isEmployerContent ? "bg-true-blue" : "bg-thistle";
  const textColor = isEmployerContent ? "text-true-blue" : "text-thistle";
  const checkmarkColor = isEmployerContent ? "text-true-blue" : "text-thistle";
  
  return (
    <div className="py-10">
      <h3 className={`text-h3 ${titleColor} mb-6 text-center`}>{title}</h3>
      <p className="text-body-lg mb-8 text-foreground/80 max-w-4xl mx-auto">
        {description}
      </p>
      
      <div className="grid md:grid-cols-2 gap-8 mb-10">
        <div className="bg-surface p-6 rounded-xl shadow-neumorphic">
          <h4 className={`text-xl font-bold mb-4 ${textColor} text-center`}>
            {isEmployerContent ? "Vorteile für Sie" : "Vorteile für Dich"}
          </h4>
          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle2 className={`w-5 h-5 ${checkmarkColor} mr-3 flex-shrink-0 mt-0.5`} />
                <span className="text-foreground">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="bg-gradient-floral p-6 rounded-xl shadow-neumorphic">
          <h4 className={`text-xl font-bold mb-4 ${textColor} text-center`}>Unser Ansatz</h4>
          <div className="space-y-6">
            <div className="flex items-start">
              <div className={`${accentColor} rounded-full p-2 w-8 h-8 flex items-center justify-center text-white mr-3 flex-shrink-0`}>
                <span>1</span>
              </div>
              <div>
                <h5 className="font-bold mb-1">
                  {isEmployerContent ? "Anforderungsanalyse" : "Profil erstellen"}
                </h5>
                <p className="text-foreground text-sm">
                  {isEmployerContent 
                    ? "Detaillierte Erfassung Ihrer Stellenanforderungen und Unternehmenskultur" 
                    : "Schnell und einfach mit Deinen Qualifikationen und Wünschen"}
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className={`${accentColor} rounded-full p-2 w-8 h-8 flex items-center justify-center text-white mr-3 flex-shrink-0`}>
                <span>2</span>
              </div>
              <div>
                <h5 className="font-bold mb-1">
                  {isEmployerContent ? "Kandidatenauswahl" : "Persönliche Beratung"}
                </h5>
                <p className="text-foreground text-sm">
                  {isEmployerContent
                    ? "KI-gestütztes Matching von passenden Kandidaten nach Ihren Kriterien"
                    : "Individuelle Karriereberatung durch Experten"}
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className={`${accentColor} rounded-full p-2 w-8 h-8 flex items-center justify-center text-white mr-3 flex-shrink-0`}>
                <span>3</span>
              </div>
              <div>
                <h5 className="font-bold mb-1">
                  {isEmployerContent ? "Nahtlose Integration" : "Maßgeschneiderte Angebote"}
                </h5>
                <p className="text-foreground text-sm">
                  {isEmployerContent
                    ? "Umfassende Betreuung von der Vorauswahl bis zur erfolgreichen Einstellung"
                    : "Passende Stellen, die zu Deinen Qualifikationen und Zielen passen"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="text-center">
        <Button 
          asChild 
          variant={isEmployerContent ? 'default' : 'secondary'}
          size="lg"
          className={cn(
            "rounded-full font-bold",
            "whitespace-nowrap"
          )}
        >
          <Link href={ctaLink}> 
            <span className="inline-flex items-center justify-center">
              {ctaText}
              <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
            </span>
          </Link>
        </Button>
      </div>
    </div>
  );
}

export function TargetGroupTabs({ showEmployerTab = true }: { showEmployerTab?: boolean }) {
  const activeContent = {
    title: "Für aktiv Suchende",
    description: "Du bist auf der Suche nach einer neuen beruflichen Herausforderung? Unser Team unterstützt Dich mit KI-gestützten Tools und persönlicher Betreuung bei Deiner Jobsuche. Schau Dir die aktuellen Ausschreibungen an. Nichts dabei? Kein Problem - wir stellen unsere AI-Expertise in den Dienst Deiner Karriere und suchen proaktiv für Dich - kostenlos!",
    benefits: [
      "Effiziente und gezielte Jobsuche ohne Zeitverschwendung",
      "Maßgeschneiderte Stellenangebote basierend auf Deinem Profil",
      "Persönliche Betreuung und Coaching im gesamten Bewerbungsprozess",
      "Optimierte Bewerbungsunterlagen und Interviewvorbereitung",
      "Kontinuierliches Feedback zu jedem Schritt"
    ],
    ctaText: "Jetzt Job finden",
    ctaLink: "/candidates/aktive-jobsuche"
  };
  
  const passiveContent = {
    title: "Für passive Wechselwillige",
    description: "Du bist in Deinem aktuellen Job zufrieden, aber offen für spannende neue Möglichkeiten? Wir nutzen unsere AI-Expertise, um für Dich diskret nach passenden Chancen für Deine Karriereentwicklung Ausschau zu halten. Wenn Du an einem unserer Vorschläge Interesse hast, übernehmen wir es gern, Dich bei dem jeweiligen Unternehmen zu positionieren - für Dich immer kostenlos!",
    benefits: [
      "Absolute Diskretion und Anonymität gegenüber Deinem aktuellen Arbeitgeber",
      "Kontinuierliche Marktsondierung ohne eigenen Zeitaufwand",
      "Hochwertige und relevante Jobangebote mit echtem Mehrwert",
      "Proaktive Netzwerkpflege und Sichtbarkeit bei relevanten Unternehmen",
      "Langfristige Karrierebegleitung und strategische Beratung"
    ],
    ctaText: "Diskret Karrierechancen entdecken",
    ctaLink: "/candidates/passive-jobsuche"
  };
  
  const employerContent = {
    title: "Für Unternehmen",
    description: "Sie sind auf der Suche nach qualifizierten Fachkräften für Ihr Unternehmen? Unsere KI-gestützten Recruiting-Lösungen helfen Ihnen, die passenden Talente zu finden und zu gewinnen. Wir bieten maßgeschneiderte Ansätze, die genau auf Ihre Unternehmenskultur und Anforderungen zugeschnitten sind.",
    benefits: [
      "Qualifizierte Kandidaten dank präziser KI-gestützter Matching-Algorithmen",
      "Schnellere Besetzung offener Stellen durch effiziente Prozesse",
      "Reduzierung von Fehlbesetzungen durch kulturelles Matching",
      "Entlastung Ihrer HR-Abteilung bei zeitaufwändigen Recruiting-Aufgaben",
      "Flexibles Leistungsmodell je nach Anforderung und Budget"
    ],
    ctaText: "Recruiting optimieren",
    ctaLink: "/companies"
  };

  return (
    <section className="py-16 bg-surface">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-h2 mb-6">Für wen ist Umynd?</h2>
          <p className="text-body-lg max-w-4xl mx-auto text-foreground/80">
            {showEmployerTab 
              ? "Ob Sie als Unternehmen Top-Talente suchen oder Du als Kandidat:in neue berufliche Chancen entdecken möchtest – Umynd bietet maßgeschneiderte KI-gestützte Unterstützung für individuelle Bedürfnisse."
              : "Ob Du aktiv nach einem neuen Job suchst oder einfach offen für spannende Möglichkeiten bist – Umynd bietet Dir maßgeschneiderte KI-gestützte Unterstützung für Deine individuellen Bedürfnisse."
            }
          </p>
        </div>
        
        <Tabs defaultValue="active" className="w-full">
          <div className="flex justify-center mb-8">
            <TabsList className={cn(
              "inline-flex rounded-full bg-floral-white-300 p-1 flex-wrap justify-center",
              "h-auto" 
            )}>
              <TabsTrigger value="active">
                <UserCircle className="w-5 h-5 mr-2" />
                Aktiv Suchende
              </TabsTrigger>
              
              <TabsTrigger value="passive">
                <BriefcaseBusiness className="w-5 h-5 mr-2" />
                Passiv Wechselwillige
              </TabsTrigger>
              
              {showEmployerTab && (
                <TabsTrigger value="employer">
                  <Building className="w-5 h-5 mr-2" />
                  Unternehmen
                </TabsTrigger>
              )}
            </TabsList>
          </div>
          
          <TabsContent value="active">
            <motion.div
              key="active"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <TabContent {...activeContent} />
            </motion.div>
          </TabsContent>
          <TabsContent value="passive">
            <motion.div
              key="passive"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <TabContent {...passiveContent} />
            </motion.div>
          </TabsContent>
          {showEmployerTab && (
            <TabsContent value="employer">
              <motion.div
                key="employer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <TabContent {...employerContent} />
              </motion.div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </section>
  );
}